import type { useNotifications } from '@toolpad/core/useNotifications';

type Notifications = ReturnType<typeof useNotifications>;

interface FirebaseLikeError {
  code?: string;
  message?: string;
}

/**
 * Normalize an unknown error value into a single user-facing message.
 *
 * Accepts plain Error, FirebaseError-shaped objects (with `code` and
 * `message`), strings, or anything else. Always returns a non-empty string.
 */
export function describeError(error: unknown, fallbackMessage = 'Something went wrong.'): string {
  if (typeof error === 'string' && error.trim()) return error;
  if (error && typeof error === 'object') {
    const e = error as FirebaseLikeError;
    const code = typeof e.code === 'string' ? e.code : '';
    const message = typeof e.message === 'string' ? e.message : '';
    if (code && message) return `${message} (${code})`;
    if (message) return message;
    if (code) return code;
  }
  return fallbackMessage;
}

/**
 * Show a user-facing error snackbar AND log full diagnostics to the console.
 * Use this anywhere we currently swallow errors with `console.error` only.
 */
export function notifyError(
  notifications: Notifications,
  error: unknown,
  fallbackMessage = 'Something went wrong.',
  options?: { autoHideDuration?: number }
): string {
  const message = describeError(error, fallbackMessage);
  // Always log full error for diagnostics
  // eslint-disable-next-line no-console
  console.error('[notifyError]', message, error);
  notifications.show(message, {
    severity: 'error',
    autoHideDuration: options?.autoHideDuration ?? 6000
  });
  return message;
}
